import styled from 'styled-components';
import CubeIcon from '@svg/cube';

import { AutoAwesomeOutlined, RingVolumeOutlined, SwitchAccountOutlined, MessageOutlined } from '@mui/icons-material';
import { Alert } from '@mui/material';
import { Template, templatesService, TemplateType } from '@requests/templates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';
import { RequestError } from '@requests/common';
import { Button } from '@common/Button';
import { TemplateCards } from './TemplateCards';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;

  & .MuiInputAdornment-root {
    margin-right: 1px;

    p {
      color: ${({ theme }) => theme.primary};
    }
  }
`;

export const TemplatesHolder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const CardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: 1fr;
  grid-gap: 1rem;

  &:first-of-type {
    margin-left: 0rem;
  }

  &:last-of-type {
    margin-right: 0rem;
  }

  @media (min-width: 1280px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

interface Props {
  category: TemplateType;
  selectedTemplate: string | null;
  setSelectedTemplate: React.Dispatch<string | null>;
}

export const SelectTemplateBody: React.FC<Props> = ({ category, selectedTemplate, setSelectedTemplate }) => {
  const queryClient = useQueryClient();

  const {
    data: templates = [],
    isLoading,
    error,
    refetch,
  } = useQuery<Template[], RequestError>({
    queryKey: ['templates'],
    queryFn: templatesService.list,
    refetchOnReconnect: true,
    retry: false,
  });

  const { mutateAsync: deleteTemplate, isPending: isTemplateDeleting } = useMutation({
    mutationFn: templatesService.delete,
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      enqueueSnackbar('Template deleted successfully!', { variant: 'success', autoHideDuration: 3000 });
    },
    onError: (error) => {
      console.error(error);
      enqueueSnackbar('Error while deleting template!', { variant: 'error', autoHideDuration: 3000 });
      enqueueSnackbar(error.message, {
        variant: 'error',
        autoHideDuration: 10000,
      });
    },
  });

  const getTemplateCards = (type: 'user' | 'telnyx') => {
    try {
      const filteredTemplates = templates.filter((template) =>
        type === 'user' ? template.org_id !== 'telnyx' : template.org_id === 'telnyx'
      );

      const getIcon = (category: TemplateType) => {
        switch (category) {
          case TemplateType.CHATBOT:
            return <AutoAwesomeOutlined />;
          case TemplateType.CALL_CONTROL:
            return <RingVolumeOutlined />;
          case TemplateType.MODULE:
            return <CubeIcon height={24} />;
          case TemplateType.MESSAGING:
            return <MessageOutlined />;
          case TemplateType.USER_DEFINED:
            return <SwitchAccountOutlined />;
        }
      };

      const cards = filteredTemplates.map((template) => ({
        id: template.id,
        title: template.name,
        icon: getIcon(template.type),
        description: template.description,
        category: template.type,
        onClick: () => {
          if (selectedTemplate === template.id) setSelectedTemplate(null);
          else setSelectedTemplate(template.id);
        },
        onDeleteClick: async () => {
          try {
            await deleteTemplate({ id: template.id });
          } catch {}
        },
        loading: isTemplateDeleting && selectedTemplate === template.id,
        selected: selectedTemplate === template.id,
        disabled: selectedTemplate !== null && selectedTemplate !== template.id,
        intercomDataAttribute: undefined,
        ...(type === 'telnyx' && { onDeleteClick: undefined, loading: false }),
      }));

      if (type === 'telnyx') {
        return cards.filter((card) => card.category === category);
      }

      const isUserCategory = category === TemplateType.USER_DEFINED;
      return isUserCategory ? cards : cards.filter((card) => card.category === category);
    } catch {
      return [];
    }
  };

  if (error) {
    return (
      <Container>
        <TemplatesHolder>
          <Alert
            severity='error'
            action={
              <Button customColor='red' onClick={() => refetch()}>
                Retry
              </Button>
            }
          >
            {error?.response?.data?.error || error?.message}
          </Alert>
        </TemplatesHolder>
      </Container>
    );
  }

  const telnyxTemplateCards = getTemplateCards('telnyx');
  const userTemplateCards = getTemplateCards('user');

  return (
    <Container>
      {category === TemplateType.MODULE && !isLoading && (
        <Alert severity='info'>
          Modules are a special type of workflow that appear as a node option in standard workflows. They can be reused
          across multiple standard workflows, and updated from a single canvas.
        </Alert>
      )}
      {category === TemplateType.CALL_CONTROL && !isLoading && (
        <Alert severity='info'>
          These call control templates will not work immediately. You must first go to the Inbound Call trigger node and
          assign a Voice Application from your Mission Control Portal account. It is also recommended to change to
          OpenAI models if you can, as they perform quicker and provide a better user experience right now.
        </Alert>
      )}
      {category === TemplateType.MESSAGING && !isLoading && (
        <Alert severity='info'>
          These messaging templates require a Messaging Profile to be configured. You can create and configure a
          Messaging Profile in your Mission Control Portal account. Once configured, assign it to the Inbound Message
          trigger node in your workflow.
        </Alert>
      )}
      <TemplateCards
        isLoading={isLoading}
        category={category}
        userCards={userTemplateCards}
        telnyxCards={telnyxTemplateCards}
      />
    </Container>
  );
};
